import { inject, Injectable } from '@angular/core';
import { LoginSession } from '@shared/dto/login.session';
import { LocalStorageService } from '../storage/local.storage.service';
import { HttpRequest } from '@angular/common/http';
import { RefreshData } from '@shared/dto/refresh.data';
import { Role } from '@shared/dto/auth/role';
import { MantisPermisosService } from '@features/mantis/services/mantis-permisos.service';
import { PerfilMantisEnum } from '@shared/enums/mantis/perfil-mantis.enum';
import { PathConstants } from '@shared/constants/path-constants';
import { environment } from '@envs/environment';
import { PortalWebAuthService } from './portal-web-auth.service';
import { PreguntasSeguridadGateService } from './preguntas-seguridad-gate.service';

@Injectable({
    providedIn: 'root'
})
export class TokenService {

    //Inyeccción dependencias
    private _storageService = inject(LocalStorageService);
    private _mantisPermisosService = inject(MantisPermisosService);
    private _portalWebAuth = inject(PortalWebAuthService);
    private _preguntasGate = inject(PreguntasSeguridadGateService);

    private readonly USER_KEY = 'data-usr-2';
    private readonly PERFIL_KEY = 'perfil_usuario';

    private _isRefreshing = false;

    private _redirModulo: string | null = null;

    get isRefreshing(): boolean {
        return this._isRefreshing;
    }
    set isRefreshing(value) {
        this._isRefreshing = value;
    }

    setLoginSesion(loginSession: LoginSession): void {
        this._storageService.setItem(this.USER_KEY, loginSession);
    }

    getDataUser(): LoginSession | null {
        return this._storageService.getItem<LoginSession>(this.USER_KEY);
    }

    setPerfilesBD(perfiles: string[]): void {
        let perfil: PerfilMantisEnum | null = null;
        if (perfiles.includes(PerfilMantisEnum.INFORMADOR)) {
            perfil = PerfilMantisEnum.INFORMADOR;
        }
        if (perfiles.includes(PerfilMantisEnum.DESARROLLADOR)) {
            perfil = PerfilMantisEnum.DESARROLLADOR;
        }
        if (perfiles.includes(PerfilMantisEnum.ASOFONDOS)) {
            perfil = PerfilMantisEnum.ASOFONDOS;
        }
        if (perfil !== null) {
            this._mantisPermisosService.setPerfil(perfil);
        }
    }

    private getToken(): string | null {
        const dataUsr = this.getDataUser();
        if (!dataUsr) {
            return null;
        }
        return dataUsr.accessToken;
    }

    isAuthenticated(): boolean {
        const token = this.getToken();
        if (!token) {
            return false;
        }
        const payload = JSON.parse(atob(token.split('.')[1]));
        const exp = payload.exp * 1000;
        return Date.now() < exp;
    }

    timeExp(): number {
        const token = this.getToken();
        if (!token) {
            return 0;
        }
        const payload = JSON.parse(atob(token.split('.')[1]));
        const exp = payload.exp * 1000;
        return exp - Date.now();
    }

    getRefreshToken(): string | null {
        const dataUsr = this.getDataUser();
        if (!dataUsr) {
            return null;
        }
        return dataUsr.refreshToken;
    }

    addTokenHeader(request: HttpRequest<unknown>) {
        // Si la petición ya trae Authorization (p. ej. Basic Auth), no sobrescribirla
        if (request.headers.has('Authorization')) {
            return request;
        }
        const token = this.getToken();
        if (token == null) {
            return request;
        }
        return request.clone({ headers: request.headers.set('Authorization', `Bearer ${token}`) });
    }

    updateTokens(response: RefreshData): void {
        const dataUsr = this.getDataUser();
        if (dataUsr != null) {
            dataUsr.accessToken = response.access_token;
            dataUsr.refreshToken = response.refresh_token;
            this._storageService.setItem(this.USER_KEY, dataUsr);
        }
    }

    logout(): void {
        this._portalWebAuth.cerrarSesionWeb();
        this._preguntasGate.limpiarCacheSesion();
        this._storageService.clear();
        // Solo limpia claves de sesion; no usa clear() para no borrar recordarme ni preferencias
        this._storageService.removeItem(this.USER_KEY);
        this._storageService.removeItem(this.PERFIL_KEY);
    }

    /**
     * PLUS-0092-H1-H2: auth Web en paralelo (iframe oculto) al login de PLUS.
     */
    iniciarSesionPortalWeb(): void {
        const dataUsr = this.getDataUser();
        const username = dataUsr?.username ?? '';
        const password = dataUsr?.pass_siafp_anterior ?? '';
        if (!username || !password) {
            return;
        }
        this._portalWebAuth.iniciarSesionSilenciosa(username, password);
    }

    getRoles(): Role[] | undefined {
        return this.getDataUser()?.roles;
    }

    /**
     * REQ.PLUS-0061-H5 - cgaleano
     * Verifica si el usuario tiene el perfil de administrador (MantisAsofondos)
     * El perfil se obtiene de la BD, no de los roles de Keycloak
     * @returns true si el usuario es administrador, false en caso contrario
     */
    isAdministradorMantis(): boolean {
        // El perfil se obtiene de la BD y se establece en MantisPermisosService
        // mediante setPerfilesBD() que se llama cuando se consultan los perfiles del usuario
        const perfil = this._mantisPermisosService.getPerfil();
        return perfil === PerfilMantisEnum.ASOFONDOS;
    }

    verificarUrlRole(url: string): boolean {
        const roles = this.getRoles();
        const urlTrimmed = url.trim();
        const isRutaInicio = urlTrimmed.startsWith(PathConstants.PATH_INICIO_PORTAL);
        // Usuario sin roles: solo permitir acceso a la vista inicial del portal
        if (!roles || roles.length === 0) {
            return isRutaInicio;
        }
        // Usuario con roles: dashboard siempre permitido
        if (isRutaInicio) {
            return true;
        }
        // Verificar permisos por módulos para el resto de rutas
        let result: boolean = false;
        for (const role of roles) {
            if (role.user !== null && role.user.modulo !== null && role.user.modulo.length > 0) {
                const modulos = role.user.modulo;
                for (const modulo of modulos) {
                    result = (modulo.opcionMOdulo?.filter(op => urlTrimmed.startsWith(op.urlOpcion.trim())).length ?? 0) > 0;
                    if (result) {
                        return true;
                    }
                }
            }
        }
        return result;
    }

    updateRedirModulo(modulo: string | null): void {
        this._redirModulo = modulo;
    }

    getRedirModulo(): string | null {
        return this._redirModulo;
    }

    /**
     * PLUS-0092-H1-H2: navega a SIAFP Web en la misma pestana (reauth si idle vencido).
     * PLUS-0092-H4: envia nombre y usuario visibles de PLUS para el header Web.
     * PLUS-0097-H5 Flujo 3: si el flag esta activo y faltan preguntas, aviso + modal antes de abrir.
     */
    abrirPortalWeb(urlOpcion: string): void {
        const abrir = (): void => {
            const dataUsr = this.getDataUser();
            this._portalWebAuth.abrirOpcion(
                urlOpcion,
                dataUsr?.username ?? '',
                dataUsr?.pass_siafp_anterior ?? '',
                this.nombreVisiblePlus(dataUsr),
                dataUsr?.username ?? ''
            );
        };
        if (!environment.preguntasSeguridadHabilitadas) {
            abrir();
            return;
        }
        this._preguntasGate.asegurarPreguntasAntesDeAbrir().subscribe((puedeContinuar) => {
            if (!puedeContinuar) {
                return;
            }
            abrir();
        });
    }

    /**
     * Arma el nombre corto que muestra el header de PLUS (primer nombre y primer apellido).
     *
     * @param dataUsr sesion PLUS; puede ser nula
     * @returns nombre visible, o el username si no hay nombres
     */
    private nombreVisiblePlus(dataUsr: LoginSession | null): string {
        if (dataUsr == null) {
            return '';
        }
        const username = dataUsr.username ?? '';
        const firstName = dataUsr.firstName && dataUsr.firstName !== '-' ? dataUsr.firstName : '';
        const lastName = dataUsr.lastName && dataUsr.lastName !== '-' ? dataUsr.lastName : '';
        if (!firstName || !lastName || firstName === username || lastName === username) {
            return username;
        }
        const nombre = this.capitalizarPalabra(firstName.split(' ')[0] ?? '');
        const apellido = this.capitalizarPalabra(lastName.split(' ')[0] ?? '');
        return `${nombre} ${apellido}`.trim();
    }

    private capitalizarPalabra(palabra: string): string {
        if (!palabra) {
            return '';
        }
        return palabra.charAt(0).toLocaleUpperCase() + palabra.slice(1).toLocaleLowerCase();
    }

}
